import { createSelector } from "reselect";
import get from "src/lib/get";
import giftProductsData from "src/data/products-gift";
import {
  EXPRESS_DELIVERY_COST_IN_PENCE,
  GIFT_WRAPPING_COST_IN_PENCE,
  GIFTS_OFFER_PERCENTAGE_DISCOUNT,
} from "src/data/app-constants";
import toDecimalPlaces from "src/lib/to-decimal-places";
import { getOptionValues } from "src/redux/gifts/helpers";

export function getSelectedProducts(state) {
  return get(state, "gifts.basket", []);
}

export function getHasExpressDelivery(state) {
  return get(state, "gifts.expressDelivery", false);
}

export function getIsGiftOffer(state) {
  return get(state, "gifts.isGiftOffer", false);
}

export const getGiftsBasketQuantity = createSelector(
  getSelectedProducts,
  (selectedProducts) =>
    selectedProducts.reduce((result, product) => {
      return result + product.options.length;
    }, 0)
);

export const getGiftsProductSKUs = createSelector(
  getSelectedProducts,
  (selectedProducts) =>
    selectedProducts.reduce((result, product) => {
      return result.concat(product.options.map((i) => product.id));
    }, [])
);

export const getSelectedProductsWithData = createSelector(
  [getSelectedProducts, getIsGiftOffer],
  (selectedProducts, isGiftOffer) => {
    return selectedProducts.map((product) => {
      const productData = giftProductsData[product.id];
      return {
        ...productData,
        ...product,
        quantity: product.options.length,
        ...(isGiftOffer && {
          priceInPence:
            productData.priceInPence -
            productData.priceInPence * GIFTS_OFFER_PERCENTAGE_DISCOUNT,
        }),
      };
    });
  }
);

export const getGiftsSubTotal = createSelector(
  [getSelectedProductsWithData, getIsGiftOffer],
  (selectedProducts = [], isGiftOffer) => {
    const giftsSubTotal = selectedProducts.reduce((result, selectedProduct) => {
      const productData = giftProductsData[selectedProduct.id];
      const productPrice = isGiftOffer
        ? productData.priceInPence -
          productData.priceInPence * GIFTS_OFFER_PERCENTAGE_DISCOUNT
        : productData.priceInPence;
      return (
        result +
        selectedProduct.options.reduce(
          (productTotal) => productTotal + productPrice,
          0
        )
      );
    }, 0);
    return toDecimalPlaces(giftsSubTotal, 2);
  }
);

export const getGiftsGiftWrappingCost = createSelector(
  [getSelectedProducts],
  (selectedProducts) => {
    const giftWrappingCost = selectedProducts.reduce(
      (result, selectedProduct) => {
        return (
          result +
          selectedProduct.options.reduce(
            (accumulatedCost, options) =>
              options.gift_wrapping
                ? accumulatedCost + GIFT_WRAPPING_COST_IN_PENCE
                : accumulatedCost,
            0
          )
        );
      },
      0
    );

    return toDecimalPlaces(giftWrappingCost, 2);
  }
);

export const getGiftsDeliveryCost = createSelector(
  getHasExpressDelivery,
  (hasExpressDelivery) => {
    return hasExpressDelivery ? EXPRESS_DELIVERY_COST_IN_PENCE : 0;
  }
);

export const getGiftsTotal = createSelector(
  [getGiftsSubTotal, getGiftsGiftWrappingCost, getGiftsDeliveryCost],
  (giftsSubTotal, giftWrappingCost, deliveryCost) => {
    return giftsSubTotal + giftWrappingCost + deliveryCost;
  }
);

export const getGiftsProductsPayload = createSelector(
  getSelectedProducts,
  (selectedProducts) => {
    return selectedProducts.reduce((result, product) => {
      product.options.forEach((option) => {
        const itemOption = getOptionValues(option);

        result.push({
          id: product.id,
          options: itemOption,
        });
      });

      return result;
    }, []);
  }
);

export const getGiftsTransactionProducts = createSelector(
  getSelectedProductsWithData,
  (selectedProducts) =>
    selectedProducts.reduce((result, product) => {
      const productData = giftProductsData[product.id];
      result.push({
        sku: productData.id,
        name: productData.name,
        category: "Gift Item",

        price: productData.price,
        quantity: product.options.length,
      });
      return result;
    }, [])
);
